// Turns the generated third-party notices file into the per-app, per-package structure
// the Credits page presents as a list instead of one enormous block of monospace.
//
// The file is written by `scripts/gen-third-party-notices.mjs`, so this parser and that
// script are two halves of one format and have to move together. Blocks are joined by a
// 60-character `─` rule and come in two shapes — an app heading:
//
//     === Studio ===
//     One line saying what this app is.
//
// and a package entry:
//
//     name@version — LICENCE
//     Author Name <author@example.com>        ← optional, omitted when unknown
//                                             ← blank line
//     <the dependency's own licence text>
//
// Every entry belongs to the heading above it.
//
// THE TEXT IS THE COMPLIANCE PAYLOAD, and everything here is arranged around not losing
// it. MIT, BSD-2/3 and Apache-2.0 all require the licence text to accompany the
// distribution (spec §9a), so a block this parser cannot read keeps its first line as a
// label and everything below it as the body, rather than being dropped or folded into a
// heading. A page that renders a package's name and not its licence is worse than the
// plain dump it replaced, because it looks like it worked.
export interface NoticeEntry {
  /** Package name as published — `react`, or `@babel/runtime` with its scope intact. */
  name: string;
  /** `null` when the header line was not in the generator's format. */
  version: string | null;
  /** The SPDX id the generator resolved, e.g. `MIT`. `null` as for `version`. */
  license: string | null;
  /** The author line the generator writes under the header, when the package had one. */
  attribution: string | null;
  /** The dependency's own licence text, verbatim. */
  text: string;
}

/** One app's worth of dependencies — the unit the page is divided into. */
export interface NoticeGroup {
  /**
   * `null` only for packages that appeared before any app heading. The generator never
   * emits that, but a notice must survive a malformed file rather than be discarded, and
   * an unnamed group is honest about not knowing which app it belongs to.
   */
  name: string | null;
  /** The line under the heading. `null` when the generator wrote none. */
  description: string | null;
  entries: NoticeEntry[];
}

// The artifact names itself on its first line, and NOTHING is parsed until it does.
//
// `/third-party-notices.txt` is not a route the SPA owns, so when the file is missing the
// thing that answers is a SPA fallback: Vite's dev server and Cloudflare Pages'
// `_redirects` both rewrite an unmatched path to `index.html` and serve it at **HTTP
// 200**. A `res.ok` check passes, this parser gets a whole HTML document, and its
// degrade-don't-drop rule below dutifully files it as one package named `<!doctype html>`
// whose licence text is the Vite dev shell — which is exactly what shipped. Status codes
// cannot distinguish the artifact from its fallback; only the bytes can.
//
// A marker rather than a `<!doctype` sniff or a content-type check: both of those are
// blocklists that pass anything they didn't think of (a proxy's error page, a login
// redirect, a `application/octet-stream` CDN mislabel of the real file). This is the
// allowlist — if the generator did not write it, it is not the file.
//
// Prefix, not equality, so `scripts/gen-third-party-notices.mjs` can put provenance on
// the same line. The version is IN the marker on purpose: a format change bumps it, and a
// v1 parser meeting a v2 file refuses it loudly instead of silently mis-reading it.
const ARTIFACT_MARKER = "wavbee-third-party-notices v1";

// Ten or more, not exactly sixty: the rule's width is a detail of the template, and a
// parser that breaks when someone shortens it would fail by producing one giant entry —
// the failure mode hardest to notice on a page nobody reads twice.
const ENTRY_RULE = /\n*─{10,}\n*/;

// Greedy `.+` for the name so the LAST `@` is the version delimiter, which is what makes
// `@babel/runtime@7.28.4` split as scope-plus-name and version rather than as an empty
// name at version `babel/runtime`.
const HEADER = /^(.+)@([^@\s]+)\s+—\s+(.+)$/;

// Only ever tested against a BLOCK'S FIRST LINE, never against the file at large: an
// Apache-2.0 appendix or a NOTICE file is full of rules and boxed headings, and a
// free-floating match would split one package's licence text into two phantom apps.
const APP_HEADING = /^={3,}\s*(.+?)\s*={3,}$/;

function toEntry(block: string): NoticeEntry {
  const [header = "", ...rest] = block.split("\n");
  const match = HEADER.exec(header);

  // The generator puts the author (if any) directly under the header and the licence
  // text after a blank line. No blank line at all means there is no author to find and
  // every remaining line is body — never a label made of the whole block.
  const bodyStart = rest.findIndex((line) => line.trim() === "");
  const head = bodyStart === -1 ? [] : rest.slice(0, bodyStart);
  const body = bodyStart === -1 ? rest : rest.slice(bodyStart + 1);

  return {
    name: match?.[1] ?? header.trim(),
    version: match?.[2] ?? null,
    license: match?.[3] ?? null,
    attribution: head.join(" ").trim() || null,
    text: body.join("\n").trim(),
  };
}

/**
 * Reads the generated notices artifact into one group per app.
 *
 * `null` — not the artifact at all (see `ARTIFACT_MARKER`). The caller must treat that as
 * a fault and say so, never as "no packages": a page claiming zero dependencies is a
 * different lie from the one this replaced, not a fix for it.
 */
export function parseNotices(raw: string): NoticeGroup[] | null {
  const trimmed = raw.trimStart();
  if (!trimmed.startsWith(ARTIFACT_MARKER)) return null;

  // The whole marker line goes, not just the marker: the generator is free to put its
  // own name and a timestamp after it, and any remainder left behind would be read as
  // the first package's header.
  const firstBreak = trimmed.indexOf("\n");

  const groups: NoticeGroup[] = [];
  // Seeded unnamed so an entry can never arrive with nowhere to go. It reaches the caller
  // only if something actually landed in it — see the filter below.
  let current: NoticeGroup = { name: null, description: null, entries: [] };
  groups.push(current);

  for (const block of (firstBreak === -1 ? "" : trimmed.slice(firstBreak + 1))
    .split(ENTRY_RULE)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)) {
    const [first = "", ...rest] = block.split("\n");
    const heading = APP_HEADING.exec(first);

    if (heading) {
      current = {
        name: heading[1] ?? null,
        description: rest.join(" ").trim() || null,
        entries: [],
      };
      groups.push(current);
    } else {
      current.entries.push(toEntry(block));
    }
  }

  // An app that contributed nothing gets no heading on the page — a heading with an empty
  // list under it reads as a rendering failure, and for the seeded group it would be a
  // heading with no name at all.
  return groups.filter((group) => group.entries.length > 0);
}
